/**
 * Personas guardadas (tabla `modelos`) y poses, con caché por sesión: las usan el panel del
 * nodo Modelo (grillas con foto), la tarjeta en el canvas (foto chica) y el ejecutor. Una sola
 * bajada por empresa; si falla, lista vacía (el nodo sigue con "Automática").
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { fetchUserModels, type ModelV2 } from '../../v2/modelsApi';
import { POSE_CATEGORIES, fetchAllPoses, type PoseV2 } from '../../v2/posesApi';

const modelosCache = new Map<string, Promise<ModelV2[]>>();
let posesCache: Promise<PoseV2[]> | null = null;

/** Un fallo (red, firma) devuelve lista vacía pero NO queda cacheado: la próxima apertura reintenta. */
export function cargarModelos(companyId: string): Promise<ModelV2[]> {
  let p = modelosCache.get(companyId);
  if (!p) {
    p = fetchUserModels(companyId).catch(() => {
      modelosCache.delete(companyId);
      return [] as ModelV2[];
    });
    modelosCache.set(companyId, p);
  }
  return p;
}

export function cargarPoses(): Promise<PoseV2[]> {
  if (!posesCache) {
    posesCache = fetchAllPoses().catch(() => {
      posesCache = null;
      return [] as PoseV2[];
    });
  }
  return posesCache;
}

/**
 * `null` mientras carga.
 *
 * Con `refrescar`, muestra lo que hay en caché al instante y además vuelve a pedir la lista
 * al montarse: un modelo creado durante la sesión aparece sin recargar la página, y el caché
 * queda al día para los demás (ADR-2026-10-06-cambiar-modelo-ve-los-modelos-nuevos). Sin la
 * opción, lo de siempre: una sola bajada por empresa y por sesión.
 */
export function useModelos(
  companyId: string | null | undefined,
  opciones: { refrescar?: boolean } = {},
): ModelV2[] | null {
  const [modelos, setModelos] = useState<ModelV2[] | null>(null);
  const refrescar = opciones.refrescar === true;
  useEffect(() => {
    if (!companyId) return;
    let vivo = true;
    cargarModelos(companyId).then((m) => vivo && setModelos(m));
    if (refrescar) {
      fetchUserModels(companyId)
        .then((frescos) => {
          modelosCache.set(companyId, Promise.resolve(frescos));
          if (vivo) setModelos(frescos);
        })
        // Si falla, queda lo del caché.
        .catch(() => {});
    }
    return () => {
      vivo = false;
    };
  }, [companyId, refrescar]);
  return modelos;
}

/** `null` mientras carga. */
export function usePoses(): PoseV2[] | null {
  const [poses, setPoses] = useState<PoseV2[] | null>(null);
  useEffect(() => {
    let vivo = true;
    cargarPoses().then((p) => vivo && setPoses(p));
    return () => {
      vivo = false;
    };
  }, []);
  return poses;
}

/** Foto de presentación de una persona guardada (miniatura si hay). */
export const fotoDe = (m: ModelV2): string => m.thumbUrl || m.thumbnailUrl || '';

/** Poses de UNA persona (el nodo Modelo es una sola). Puro. */
export function posesDeUnaPersona(poses: PoseV2[]): PoseV2[] {
  return poses.filter((p) => (p.cantidadPersonas ?? 1) === 1);
}

/** Etiqueta corta de la categoría de una pose. Puro. */
export const categoriaDe = (p: PoseV2): string => POSE_CATEGORIES.find((c) => c.id === p.categoria)?.label ?? '';

/**
 * Los primeros `limite` (o todos si `todo`), pero el elegido siempre está: si queda afuera del
 * recorte, entra en lugar del último. Puro.
 */
export function recortarConElegido<T>(lista: T[], limite: number, todo: boolean, esElegido: (x: T) => boolean): T[] {
  if (todo || lista.length <= limite) return lista;
  const primeros = lista.slice(0, limite);
  const elegido = lista.find(esElegido);
  if (!elegido || primeros.includes(elegido)) return primeros;
  return [...primeros.slice(0, limite - 1), elegido];
}

/**
 * Como `recortarConElegido`, pero con VARIAS elegidas (nodo Pose): las primeras `limite` en su orden
 * y, al final, las elegidas que hayan quedado afuera. No reordena la grilla, así nada se mueve debajo
 * del cursor al elegir. Puro.
 */
export function recortarConVarias<T>(lista: T[], limite: number, todo: boolean, idDe: (x: T) => string, elegidos: readonly string[]): T[] {
  if (todo || lista.length <= limite) return lista;
  const sel = new Set(elegidos);
  const visibles = lista.slice(0, limite);
  const afuera = lista.slice(limite).filter((x) => sel.has(idDe(x)));
  return [...visibles, ...afuera];
}

/** Empresa de la cuenta (para que la tarjeta del nodo Modelo muestre la foto de la persona). */
export const CuentaContext = createContext<{ companyId: string } | null>(null);
export const useCompanyId = (): string | null => useContext(CuentaContext)?.companyId ?? null;
